// Centralized image assets for the Kernel Design System
// This file contains all image imports and exports them with descriptive names

// Logo assets
import AppHeaderKernelLogo from "./logo/app_header_kernel_logo.svg";
import GainAppLogo from "./logo/gain_app_logo.svg";
import KelpAppLogo from "./logo/kelp_app_logo.svg";
import KernelAppLogo from "./logo/kernel_app_logo.svg";
import KernelDao from "./logo/kernel_dao_logo.avif";
import FooterLogo from "./logo/footer_logo.svg";
import KernelDaoOutlined from "./logo/kernel_dao_outlined.svg";

//brand assets
import KernelSquareLogo from "./brand/kernel_square.png";
import KelpSquareLogo from "./brand/kelp_square.png";

// Vault assets
import AgEthImg from "../assets/agETH.png";
import HgEthImg from "../assets/vaults/hgeth.svg";
import ggEthImg from "../assets/vaults/ggETH.svg";
import movementImg from "../assets/vaults/movementL2logo.png";
import tacBtc from "../assets/vaults/tacBtc.svg";
import tacEth from "../assets/vaults/tacEth.svg";

// Illustration assets
import GainOpportunitiesLargeDesktop from "./illustrations/gain-op-xl.avif";
import GainOpportunitiesMobile from "./illustrations/gain-op-mobile.avif";
import GainOpportunitiesTablet from "./illustrations/gain-op-tablet.avif";
import GainOpportunitiesDesktop from "./illustrations/gain-op-desktop.avif";

import KelpOpportunitiesLargeDesktop from "./illustrations/kelp-op-xl.avif";
import KelpOpportunitiesMobile from "./illustrations/kelp-op-mobile.avif";
import KelpOpportunitiesTablet from "./illustrations/kelp-op-tablet.avif";
import KelpOpportunitiesDesktop from "./illustrations/kelp-op-desktop.avif";

import Liquidity from "./illustrations/liquidity.avif";
import Audited from "./illustrations/audited.avif";
import Utility from "./illustrations/utility.avif";
import Staking from "./illustrations/restake.avif";
import { XKernelLogo, XKelp } from "./socials";

import ETHImg from "../assets/eth.svg";
import ETHxImg from "../assets/ethx.svg";
import stETHImg from "../assets/stETH.svg";

// Favicons
import FaviconKernel from "./favicons/Kernel.png";
import FaviconGain from "./favicons/Gain.png";
import FaviconKelp from "./favicons/Kelp.png";
import KernelWhite from "./favicons/kernel-white.avif";
import KelpWhite from "./favicons/kelp-white.avif";
import GainWhite from "./favicons/gain-white.avif";

import KernelStakeIcon from "./icons/stake.png";

// Re-export all images with descriptive names
export {
  AgEthImg,
  AppHeaderKernelLogo,
  GainAppLogo,
  HgEthImg,
  KelpAppLogo,
  KernelDao,
  FooterLogo,
};

// Image categories for easier organization
export const LOGOS = {
  AppHeaderKernel: AppHeaderKernelLogo,
  GainApp: GainAppLogo,
  KelpApp: KelpAppLogo,
  KernelApp: KernelAppLogo,
  KernelDao: KernelDao,
  FooterLogo: FooterLogo,
  KernelDaoOutlined: KernelDaoOutlined,
} as const;

export const FAVICONS = {
  Kernel: FaviconKernel,
  Gain: FaviconGain,
  Kelp: FaviconKelp,
  KernelWhite: KernelWhite,
  KelpWhite: KelpWhite,
  GainWhite: GainWhite,
} as const;

export const ICONS = {
  KernelStakeIcon,
} as const;

export const COINS = {
  ETH: ETHImg,
  ETHx: ETHxImg,
  stETH: stETHImg,
} as const;

export const VAULTS = {
  HgEth: HgEthImg,
  AgEth: AgEthImg,
  ggEth: ggEthImg,
  movement: movementImg,
  tacBtc: tacBtc,
  tacEth: tacEth,
} as const;

export const BRANDS = {
  Kernel: KernelSquareLogo,
  Kelp: KelpSquareLogo,
} as const;

export const SOCIALS = {
  XKernel: XKernelLogo,
  XKelp: XKelp,
} as const;

export const ILLUSTRATIONS = {
  GainOpportunities: {
    mobile: GainOpportunitiesMobile,
    tablet: GainOpportunitiesTablet,
    desktop: GainOpportunitiesDesktop,
    largeDesktop: GainOpportunitiesLargeDesktop,
  },
  KelpOpportunities: {
    mobile: KelpOpportunitiesMobile,
    tablet: KelpOpportunitiesTablet,
    desktop: KelpOpportunitiesDesktop,
    largeDesktop: KelpOpportunitiesLargeDesktop,
  },
  Liquidity: Liquidity,
  Audited: Audited,
  Utility: Utility,
  Staking: Staking,
} as const;

// All images in a single object for easy access
export const IMAGES = {
  ...LOGOS,
  ...VAULTS,
  ...ILLUSTRATIONS,
  ...FAVICONS,
  ...ICONS,
} as const;

export const KERNELDAO_ASSETS = {
  LOGOS: LOGOS,
  VAULTS: VAULTS,
  ILLUSTRATIONS: ILLUSTRATIONS,
  FAVICONS: FAVICONS,
  ICONS: ICONS,
} as const;

// Type definitions for better TypeScript support
export type ImageName = keyof typeof IMAGES;
export type LogoName = keyof typeof LOGOS;
export type VaultName = keyof typeof VAULTS;
export type IllustrationName = keyof typeof ILLUSTRATIONS;
export type FaviconName = keyof typeof FAVICONS;
export type IconName = keyof typeof ICONS;
